import { Box } from '@mui/material'
import {
  Calendar,
  dateFnsLocalizer,
  EventPropGetter,
  Views,
} from 'react-big-calendar'
import React, { useCallback, useState } from 'react'
import 'react-big-calendar/lib/css/react-big-calendar.css'
import { format, parse, startOfWeek, getDay, startOfISOWeek } from 'date-fns'
import i18n, { dateLocales } from 'lib/i18'
import { CustomToolbar } from './CustomToolbar'
import { WeekHeader } from './WeekHeader'
import { SingleDayHeader } from './SingleDayHeader'
import './BigCalendar.css'
import { Event } from './Event'

type EventType = {
  id: number
  title: string
  start: Date
  end: Date
  resource: string
  immediate?: boolean
}

type BigCalendarProps = {
  events: EventType[]
  dateData: { startOfWeek: Date; date: Date }
  setDateData: (value: { startOfWeek: Date; date: Date }) => void
  eventStyleGetter?: EventPropGetter<{
    id: number
    title: string
    start: Date
    end: Date
  }>
  handleSelectSlot?: ({ start, end }: { start: Date; end: Date }) => void
  onSelectEvent?: (e: {
    id: number
    end: Date
    start: Date
    title: string
  }) => void
}

export const BigCalendar = ({
  events,
  dateData,
  setDateData,
  eventStyleGetter,
  handleSelectSlot,
  onSelectEvent,
}: BigCalendarProps) => {
  const [view, setView] = useState(Views.WEEK)

  const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek: () => {
      return startOfWeek(new Date(), { weekStartsOn: 1 })
    },
    getDay,
    locales: dateLocales,
  })
  const onView = useCallback((newView) => setView(newView), [setView])
  const onNavigate = useCallback(
    (newDate) =>
      setDateData({
        startOfWeek: startOfISOWeek(new Date(newDate)),
        date: new Date(newDate),
      }),
    [setDateData]
  )
  const formats = {
    timeGutterFormat: 'HH:mm',
    eventTimeRangeFormat: ({ start, end }: { start: Date; end: Date }) =>
      `${format(start, 'HH:mm')} – ${format(end, 'HH:mm')}`,
  }

  return (
    <Box height='800px'>
      <Calendar
        onSelectEvent={onSelectEvent}
        key={events ? events.length : 0}
        formats={formats}
        date={dateData.date}
        onNavigate={onNavigate}
        eventPropGetter={eventStyleGetter}
        longPressThreshold={20}
        components={{
          toolbar: CustomToolbar,
          week: { header: WeekHeader },
          day: { header: SingleDayHeader },
          event: Event,
        }}
        selectable
        onSelectSlot={handleSelectSlot}
        localizer={localizer}
        culture={i18n.language}
        events={events}
        view={view}
        onView={onView}
        views={[Views.WEEK, Views.DAY]}
      />
    </Box>
  )
}
